import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus, TrendingUp } from "lucide-react";
import { theme, card, btnPrimary, type as text, pageHeading, pageSubtitle } from "../theme";
import { api, apiErrorMessage } from "../api/client";
import { useCurrentUser } from "../auth/useCurrentUser";
import { LoadingBlock } from "../components/Spinner";
import { PortalLink } from "../components/PortalLink";
import { DetailFields, DetailSection, EditButton } from "../components/RecordView";
import { ModalOverlay, ModalHeader, Field, EmptyState, input, textarea, secondaryBtn, dangerBtn } from "./resilience/shared";
import { StatusTag } from "../components/Drawing";
import { NextSteps } from "../components/NextSteps";
import { LOSS_PERCENTILE_LABELS, fmtGbp, loadRiskAppetite, type RiskAppetiteView } from "../components/RiskAppetite";

// Exposure — what the organisation's risks could cost in a year, modelled from three-point
// ranges (FAIR-style, backend lib/exposureModel.ts), the portfolio's loss exceedance curve against
// the cyber insurance policy, and what-if scenarios over the dependency graph and the business
// impact analyses. Everything is computed by the API per request; this page only shows it.

// ── Types (mirror of backend/src/services/exposure.ts + routes/exposure.routes.ts) ──

interface Range { min: number; mode: number; max: number }

interface LossSummary {
  mean: number; p50: number; p90: number; p95: number; p99: number; probabilityOfLoss: number;
  exceedance: Array<{ threshold: number; probability: number }>;
}

interface QuantifiedRisk {
  id: string;
  riskId: string;
  frequencyMin: number; frequencyMode: number; frequencyMax: number;
  method: "manual" | "bia";
  magnitudeMin: number | null; magnitudeMode: number | null; magnitudeMax: number | null;
  biaId: string | null;
  outageHoursMin: number | null; outageHoursMode: number | null; outageHoursMax: number | null;
  fixedCost: number | null;
  currency: string;
  rationale: string | null;
  risk: { id: string; displayId: number | null; title: string; category: string | null; status: string; residualScore: number | null };
  bia: { id: string; displayId: number | null; name: string; financialLossPerHour: number | null; currency: string } | null;
  magnitude: Range | null;
  summary: LossSummary | null;
  simpleAle: number | null;
}

interface OpenRisk { id: string; displayId: number | null; title: string; category: string | null; residualScore: number | null; quantification: { id: string } | null }
interface BiaOption { id: string; displayId: number | null; name: string; financialLossPerHour: number | null; currency: string }
interface ScenarioKind { id: string; label: string; help: string }

interface InsuranceView {
  policy: { id: string; insurer: string; coverLimit: number | null; excess: number | null; currency: string; coversBusinessInterruption: boolean | null; coversRansom: boolean | null };
  comparison: { coverLimit: number | null; excess: number; expectedRetained: number; expectedRecovered: number; probabilityExceedsCover: number; p95Retained: number } | null;
}

interface Scenario {
  id: string;
  name: string;
  kind: string;
  assetId: string | null;
  supplierId: string | null;
  outageHours: number;
  recordsAffected: number | null;
  costPerRecord: number | null;
  extraCosts: number | null;
  annualProbability: number | null;
  notes: string | null;
  asset: { id: string; name: string } | null;
  supplier: { id: string; name: string } | null;
  /** The risk register entries this scenario prices. */
  riskIds?: string[];
  risks?: Array<{ id: string; displayId: number | null; title: string }>;
  result: {
    directCost: number;
    services: Array<{ id: string; name: string; lossPerHour: number | null; rtoHours: number | null; mtpdHours: number | null; currency: string; via: string | null; cost: number; beyondTolerance: boolean }>;
    unpricedServices: number;
    recordsCost: number;
    extraCosts: number;
    total: number;
    expectedAnnual: number | null;
  };
}

interface ExposureResponse {
  /** The model's quantified risks. The API currently also calls these `risks` and then overwrites
   *  that key with the open-risk list; the page reads them from `quantified`. */
  quantified?: QuantifiedRisk[];
  portfolio: LossSummary | null;
  insurance: InsuranceView | null;
  coverage: { quantified: number; openRisks: number; priced: number };
  scenarios: Scenario[];
  risks: OpenRisk[];
  bias: BiaOption[];
  assets: Array<{ id: string; name: string; kind: string; criticality: string }>;
  suppliers: Array<{ id: string; name: string }>;
  scenarioKinds: ScenarioKind[];
}

// ── Formatting ────────────────────────────────────────────────────────────────

function money(v: number | null | undefined, currency: string): string {
  if (v == null) return "—";
  try { return new Intl.NumberFormat("en-GB", { style: "currency", currency, maximumFractionDigits: 0 }).format(v); }
  catch { return `${currency} ${Math.round(v).toLocaleString("en-GB")}`; }
}

function moneyShort(v: number, currency: string): string {
  try { return new Intl.NumberFormat("en-GB", { style: "currency", currency, notation: "compact", maximumFractionDigits: 1 }).format(v); }
  catch { return `${Math.round(v)}`; }
}

const pct = (p: number) => `${Math.round(p * 1000) / 10}%`;
const riskRef = (r: { displayId: number | null }) => (r.displayId != null ? `RISK-${String(r.displayId).padStart(3, "0")}` : "RISK");
const biaRef = (b: { displayId: number | null }) => (b.displayId != null ? `BIA-${String(b.displayId).padStart(3, "0")}` : "BIA");
const num = (s: string): number | null => (s.trim() === "" ? null : Number(s));
const str = (v: number | null | undefined) => (v == null ? "" : String(v));

function hoursLabel(h: number): string {
  if (h < 24) return `${h} hour${h === 1 ? "" : "s"}`;
  if (h % 168 === 0) return `${h / 168} week${h === 168 ? "" : "s"}`;
  if (h % 24 === 0) return `${h / 24} day${h === 24 ? "" : "s"}`;
  return `${h} hours`;
}

const thStyle: React.CSSProperties = { padding: "10px 14px", borderBottom: "1px solid var(--c-line)", fontWeight: 600, whiteSpace: "nowrap" };
const tdStyle: React.CSSProperties = { padding: "10px 14px", color: theme.color.muted, whiteSpace: "nowrap" };

// ── Page ──────────────────────────────────────────────────────────────────────

export function ExposurePage() {
  const { user } = useCurrentUser();
  const canWrite = !!user?.permissions?.includes("risks:write");
  const [data, setData] = useState<ExposureResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [quantifying, setQuantifying] = useState<{ riskId: string | null } | null>(null);
  const [scenarioEdit, setScenarioEdit] = useState<{ scenario: Scenario | null } | null>(null);
  // ?risk=<riskId> opens a quantified risk's read-only view, ?scenario=<id> a scenario's.
  const [params, setParams] = useSearchParams();
  const viewRiskId = params.get("risk");
  const viewScenarioId = params.get("scenario");
  const openView = (key: "risk" | "scenario", id: string | null) => {
    const next = new URLSearchParams(params);
    next.delete("risk"); next.delete("scenario");
    if (id) next.set(key, id);
    setParams(next, { replace: true });
  };

  // The annual loss limit from the risk appetite (GET /risks/appetite): the server compares it with
  // the modelled portfolio at the chosen percentile. Optional - the page stands without it.
  const [appetite, setAppetite] = useState<RiskAppetiteView | null>(null);

  const load = useCallback(async () => {
    try { setData(await api.get<ExposureResponse>("/exposure")); setError(null); }
    catch (e) { setError(apiErrorMessage(e, "Could not load the exposure model")); }
    loadRiskAppetite().then(setAppetite).catch(() => setAppetite(null));
  }, []);
  useEffect(() => { void load(); }, [load]);

  const header = (
    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, flexWrap: "wrap", marginBottom: 20 }}>
      <div>
        <h1 style={{ ...pageHeading, display: "flex", alignItems: "center", gap: 8 }}>
          <TrendingUp size={20} /> Exposure
        </h1>
        <p style={{ ...pageSubtitle, maxWidth: 760 }}>
          What your risks could cost in a year, modelled from ranges you estimate (FAIR-style), and what a bad year looks like against your insurance.
        </p>
      </div>
      {canWrite && data && (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button type="button" style={secondaryBtn} onClick={() => setScenarioEdit({ scenario: null })}>Add a scenario</button>
          <button type="button" style={btnPrimary} onClick={() => setQuantifying({ riskId: null })}><Plus size={14} style={{ verticalAlign: -2, marginRight: 6 }} />Quantify a risk</button>
        </div>
      )}
    </div>
  );

  if (!data) {
    return <div className="page-pad">{header}{error ? <div style={{ ...card, padding: 14, ...text.body, color: theme.color.dangerText }}>{error}</div> : <LoadingBlock />}</div>;
  }

  const quantified = data.quantified ?? [];
  const currency = data.insurance?.policy.currency ?? quantified[0]?.currency ?? "GBP";
  const p = data.portfolio;
  const ins = data.insurance;
  const coverTop = ins?.policy.coverLimit != null ? ins.policy.coverLimit + (ins.policy.excess ?? 0) : null;
  const nothing = quantified.length === 0 && data.scenarios.length === 0;

  return (
    <div className="page-pad">
      {header}
      {error && <div style={{ ...card, padding: 12, marginBottom: 16, ...text.body, color: theme.color.dangerText }}>{error}</div>}

      <div style={{ display: "grid", gap: 20 }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12 }}>
          <Tile label="Annualised loss expectancy" value={p ? money(p.mean, currency) : "—"} sub="the average year" />
          <Tile label="One year in ten (P90)" value={p ? money(p.p90, currency) : "—"} sub="losses at least this high" />
          <Tile label="One year in twenty (P95)" value={p ? money(p.p95, currency) : "—"} sub="a bad year" />
          <Tile label="Quantified risks" value={`${data.coverage.quantified} of ${data.coverage.openRisks}`} sub={data.coverage.priced < data.coverage.quantified ? `open risks · ${data.coverage.quantified - data.coverage.priced} not yet priced` : "open risks"} />
          <Tile
            label="Insurance limit"
            value={ins ? (ins.policy.coverLimit != null ? money(ins.policy.coverLimit, ins.policy.currency) : "No limit recorded") : "No policy"}
            sub={ins ? `${ins.policy.insurer}${ins.policy.excess ? ` · excess ${money(ins.policy.excess, ins.policy.currency)}` : ""}` : "record one under Cyber Insurance"}
          />
          <Tile label="Chance a year exceeds cover" value={ins?.comparison ? pct(ins.comparison.probabilityExceedsCover) : "—"} sub={ins?.comparison ? "losses above excess + limit" : "needs a policy and quantified risks"} />
          <Tile label="Expected retained loss" value={ins?.comparison ? money(ins.comparison.expectedRetained, currency) : "—"} sub={ins?.comparison ? `a year, after cover · P95 kept ${money(ins.comparison.p95Retained, currency)}` : "what you would pay yourselves"} />
        </div>

        {appetite && <LossAppetiteLine appetite={appetite} />}

        {nothing ? (
          <div style={card}>
            <EmptyState
              title="Nothing modelled yet"
              detail="Pick a risk from your register and estimate two ranges: how often it could happen in a year, and what one occurrence would cost (or take the cost from a business impact analysis's loss per hour). The platform simulates ten thousand years to show the average and the bad years. Scenarios then ask what one specific event would cost."
              action={
                <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
                  {canWrite && <button type="button" style={btnPrimary} onClick={() => setQuantifying({ riskId: null })}>Quantify a risk</button>}
                  <PortalLink to="/risk-register" style={{ ...secondaryBtn, textDecoration: "none" }}>Risk register</PortalLink>
                  <PortalLink to="/resilience/bia" style={{ ...secondaryBtn, textDecoration: "none" }}>Business impact</PortalLink>
                </div>
              }
            />
          </div>
        ) : (
          <>
            {p && (
              <div style={{ ...card, padding: 18 }}>
                <div style={{ ...text.sectionTitle, margin: "0 0 4px" }}>Loss exceedance curve</div>
                <div style={{ ...text.hint, marginBottom: 10, maxWidth: 760 }}>
                  How likely a year's total losses are to exceed each amount. Read across from a loss to see its chance: where the line crosses 10% is a one-in-ten year.
                  {coverTop != null && " The dashed line is where your insurance runs out (excess plus limit); the height of the curve there is the chance a year goes beyond cover."}
                </div>
                <ExceedanceChart summary={p} currency={currency} coverTop={coverTop} />
              </div>
            )}

            <div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 10 }}>
                <div style={{ ...text.sectionTitle, margin: 0 }}>Quantified risks</div>
              </div>
              {quantified.length === 0 ? (
                <div style={card}><EmptyState title="No risks quantified" detail="Give an open risk a frequency and a cost range to see what it could cost in a year." action={canWrite ? <button type="button" style={btnPrimary} onClick={() => setQuantifying({ riskId: null })}>Quantify a risk</button> : undefined} /></div>
              ) : (
                <div style={{ ...card, padding: 0, overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: text.body.fontSize }}>
                    <thead><tr style={{ textAlign: "left", color: theme.color.subtle, fontSize: text.label.fontSize }}>
                      {["Risk", "Events a year", "Cost per event", "ALE", "P90", "Basis"].map((h) => <th key={h} style={thStyle}>{h}</th>)}
                    </tr></thead>
                    <tbody>
                      {quantified.map((q) => (
                        <tr key={q.id} onClick={() => openView("risk", q.riskId)} style={{ borderBottom: "1px solid var(--c-border)", cursor: "pointer" }}>
                          <td style={{ padding: "10px 14px", color: theme.color.text, minWidth: 200 }}>
                            <span style={{ color: theme.color.muted, fontWeight: 600, marginRight: 6 }}>{riskRef(q.risk)}</span>{q.risk.title}
                          </td>
                          <td style={tdStyle}>{q.frequencyMin} / {q.frequencyMode} / {q.frequencyMax}</td>
                          <td style={tdStyle}>{q.magnitude ? `${money(q.magnitude.min, q.currency)} – ${money(q.magnitude.max, q.currency)}` : <span style={{ color: theme.color.warningText }}>not priced</span>}</td>
                          <td style={{ ...tdStyle, color: theme.color.text, fontWeight: 600 }}>{q.summary ? money(q.summary.mean, q.currency) : "—"}</td>
                          <td style={tdStyle}>{q.summary ? money(q.summary.p90, q.currency) : "—"}</td>
                          <td style={{ ...tdStyle, whiteSpace: "normal" }}>{q.method === "bia" ? (q.bia ? `${biaRef(q.bia)} ${q.bia.name}` : "analysis removed") : "Estimate"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <div style={{ ...text.meta, marginTop: 6 }}>Events a year are minimum / most likely / maximum. ALE is the average annual loss over ten thousand simulated years; P90 is the loss exceeded one year in ten.</div>
            </div>

            <div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 10 }}>
                <div style={{ ...text.sectionTitle, margin: 0 }}>Scenarios</div>
                {canWrite && <button type="button" style={secondaryBtn} onClick={() => setScenarioEdit({ scenario: null })}>Add a scenario</button>}
              </div>
              {data.scenarios.length === 0 ? (
                <div style={card}><EmptyState title="No scenarios" detail="Ask what one event would cost: an asset or supplier failing, ransomware across the estate, or a personal data breach. The platform follows the dependency graph to every business process it takes down and prices the outage from their business impact analyses." /></div>
              ) : (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 12 }}>
                  {data.scenarios.map((s) => (
                    <ScenarioCard key={s.id} s={s} kinds={data.scenarioKinds} currency={currency} onOpen={() => openView("scenario", s.id)} />
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {viewRiskId && !quantifying && (() => {
        const q = quantified.find((x) => x.riskId === viewRiskId);
        return q ? <QuantificationView q={q} canWrite={canWrite} onEdit={() => setQuantifying({ riskId: q.riskId })} onClose={() => openView("risk", null)} /> : null;
      })()}
      {viewScenarioId && !scenarioEdit && (() => {
        const sc = data.scenarios.find((x) => x.id === viewScenarioId);
        return sc ? <ScenarioView s={sc} kinds={data.scenarioKinds} currency={currency} canWrite={canWrite} onEdit={() => setScenarioEdit({ scenario: sc })} onClose={() => openView("scenario", null)} /> : null;
      })()}
      {quantifying && (
        <QuantifyModal
          data={data} quantified={quantified} initialRiskId={quantifying.riskId} defaultCurrency={currency}
          onClose={() => setQuantifying(null)}
          onSaved={async (savedRiskId) => { setQuantifying(null); await load(); openView("risk", savedRiskId ?? null); }}
        />
      )}
      {scenarioEdit && (
        <ScenarioModal
          data={data} existing={scenarioEdit.scenario}
          onClose={() => setScenarioEdit(null)}
          onSaved={async (kept) => { setScenarioEdit(null); await load(); if (!kept) openView("scenario", null); }}
        />
      )}
    </div>
  );
}

// The organisation's stated annual loss limit against the modelled loss at the same percentile.
function LossAppetiteLine({ appetite }: { appetite: RiskAppetiteView }) {
  const { lossLimitGbp, lossPercentile } = appetite.settings;
  const loss = appetite.loss;
  const setLink = <PortalLink to="/risk-register" style={{ color: theme.color.ref }}>risk appetite on the risk register</PortalLink>;
  if (lossLimitGbp == null) {
    return (
      <div style={{ ...card, padding: 12, ...text.hint }}>
        No annual loss limit is set. Set one in the {setLink} to compare the modelled loss with what you will tolerate.
      </div>
    );
  }
  return (
    <div style={{ ...card, padding: 12, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", ...text.body }}>
      {loss && <StatusTag tone={loss.above ? "gap" : "met"}>{loss.above ? "Above your limit" : "Within your limit"}</StatusTag>}
      <span style={{ color: loss ? (loss.above ? theme.color.dangerText : theme.color.successText) : undefined, fontWeight: loss ? 600 : undefined }}>
        Your stated limit: {fmtGbp(lossLimitGbp)} at {LOSS_PERCENTILE_LABELS[lossPercentile]}
        {loss ? <>; modelled: {fmtGbp(loss.portfolioGbp)}</> : "; nothing is modelled yet to compare it with"}
      </span>
      <span style={{ ...text.hint, marginLeft: "auto" }}>Change it in the {setLink}.</span>
    </div>
  );
}

function Tile({ label, value, sub }: { label: string; value: string | number; sub?: string }) {
  return (
    <div style={{ ...card, padding: "14px 16px", height: "100%" }}>
      <div style={text.statLabel}>{label}</div>
      <div style={{ ...text.stat, color: theme.color.heading, marginTop: 4, whiteSpace: /\s/.test(String(value)) ? "normal" : "nowrap" }}>{value}</div>
      {sub && <div style={{ ...text.hint, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

// ── Loss exceedance chart ─────────────────────────────────────────────────────

function ExceedanceChart({ summary, currency, coverTop }: { summary: LossSummary; currency: string; coverTop: number | null }) {
  const pts = summary.exceedance;
  const W = 640, H = 260, L = 44, R = 16, T = 12, B = 40;
  const pw = W - L - R, ph = H - T - B;
  const n = pts.length;
  const xAt = (i: number) => L + (n <= 1 ? pw / 2 : (i / (n - 1)) * pw);
  const yAt = (prob: number) => T + (1 - prob) * ph;

  // Place the cover line between the two thresholds it falls between (log scale inside a step).
  const coverX = useMemo(() => {
    if (coverTop == null || n < 2) return null;
    const last = pts[n - 1].threshold;
    if (coverTop >= last) return { x: xAt(n - 1), beyond: coverTop > last };
    for (let i = 0; i < n - 1; i++) {
      const a = pts[i].threshold, b = pts[i + 1].threshold;
      if (coverTop >= a && coverTop <= b) {
        const f = a > 0 ? Math.log(coverTop / a) / Math.log(b / a) : coverTop / b;
        return { x: xAt(i + f), beyond: false };
      }
    }
    return { x: xAt(0), beyond: false };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coverTop, pts]);

  if (n < 2) return <div style={{ ...text.body, color: theme.color.muted }}>Not enough simulated loss to draw a curve.</div>;

  const path = pts.map((p, i) => `${i === 0 ? "M" : "L"}${xAt(i).toFixed(1)},${yAt(p.probability).toFixed(1)}`).join(" ");
  const labelEvery = Math.max(1, Math.ceil(n / 7));

  return (
    <div>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-labelledby="lec-title lec-desc" style={{ display: "block", maxHeight: 320 }}>
        <title id="lec-title">Loss exceedance curve</title>
        <desc id="lec-desc">
          {`Probability that a year's total losses exceed each amount. Average year ${money(summary.mean, currency)}; one year in ten ${money(summary.p90, currency)}; one year in twenty ${money(summary.p95, currency)}.${coverTop != null ? ` Insurance runs out at ${money(coverTop, currency)}.` : ""}`}
        </desc>
        {[0, 0.25, 0.5, 0.75, 1].map((g) => (
          <g key={g}>
            <line x1={L} x2={W - R} y1={yAt(g)} y2={yAt(g)} stroke="var(--c-border)" strokeWidth={1} />
            <text x={L - 6} y={yAt(g) + 4} textAnchor="end" fontSize={11} fill="var(--c-muted)">{Math.round(g * 100)}%</text>
          </g>
        ))}
        {pts.map((p, i) => (i % labelEvery === 0 || i === n - 1) && (
          <text key={i} x={xAt(i)} y={H - B + 16} textAnchor="middle" fontSize={11} fill="var(--c-muted)">{moneyShort(p.threshold, currency)}</text>
        ))}
        <text x={L + pw / 2} y={H - 4} textAnchor="middle" fontSize={11} fill="var(--c-muted)">Loss in a year</text>
        <path d={path} fill="none" stroke="var(--c-accent)" strokeWidth={2} strokeLinejoin="round" />
        {pts.map((p, i) => <circle key={i} cx={xAt(i)} cy={yAt(p.probability)} r={2.5} fill="var(--c-accent)"><title>{`${moneyShort(p.threshold, currency)}: ${pct(p.probability)}`}</title></circle>)}
        {coverX && (
          <g>
            <line x1={coverX.x} x2={coverX.x} y1={T} y2={T + ph} stroke="var(--c-danger)" strokeWidth={1.5} strokeDasharray="5 4" />
            <text x={Math.min(coverX.x + 4, W - R - 4)} y={T + 12} textAnchor={coverX.x > W - R - 110 ? "end" : "start"} fontSize={11} fill="var(--c-dangerText)">
              {coverX.beyond ? "Cover ends beyond the chart" : "Cover ends"}
            </text>
          </g>
        )}
      </svg>
    </div>
  );
}

// ── Scenario card ─────────────────────────────────────────────────────────────

function ScenarioCard({ s, kinds, currency, onOpen }: { s: Scenario; kinds: ScenarioKind[]; currency: string; onOpen: () => void }) {
  const kind = kinds.find((k) => k.id === s.kind)?.label ?? s.kind;
  const subject = s.asset?.name ?? s.supplier?.name ?? null;
  return (
    <div onClick={onOpen} role="button" style={{ ...card, padding: 16, cursor: "pointer", display: "grid", gap: 8, alignContent: "start" }}>
      <div>
        <div style={text.itemTitle}>{s.name}</div>
        <div style={text.hint}>{kind}{subject ? ` · ${subject}` : ""} · {hoursLabel(s.outageHours)} outage</div>
      </div>
      <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
        <div>
          <div style={text.statLabel}>Total cost</div>
          <div style={text.stat}>{money(s.result.total, currency)}</div>
        </div>
        <div>
          <div style={text.statLabel}>Expected a year</div>
          <div style={text.stat}>{s.result.expectedAnnual != null ? money(s.result.expectedAnnual, currency) : "—"}</div>
        </div>
      </div>
      <div style={text.hint}>
        {s.result.services.length > 0
          ? `${s.result.services.length} service${s.result.services.length === 1 ? "" : "s"} hit${s.result.services.some((v) => v.beyondTolerance) ? ", some beyond tolerance" : ""}`
          : "No business process with an impact analysis hit"}
      </div>
    </div>
  );
}

// ── Read-only views ───────────────────────────────────────────────────────────

function ViewHeaderActions({ canWrite, onEdit }: { canWrite: boolean; onEdit: () => void }) {
  return canWrite ? <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 4 }}><EditButton onClick={onEdit} /></div> : null;
}

function QuantificationView({ q, canWrite, onEdit, onClose }: { q: QuantifiedRisk; canWrite: boolean; onEdit: () => void; onClose: () => void }) {
  const range = (a: number | null, b: number | null, c: number | null, fmt: (v: number) => string) =>
    a == null && b == null && c == null ? null : `${a != null ? fmt(a) : "—"} / ${b != null ? fmt(b) : "—"} / ${c != null ? fmt(c) : "—"}`;
  const m = (v: number) => money(v, q.currency);
  return (
    <ModalOverlay onClose={onClose}>
      <ModalHeader title={`${riskRef(q.risk)} ${q.risk.title}`} onClose={onClose} />
      <div style={{ padding: "8px 20px 20px" }}>
        <ViewHeaderActions canWrite={canWrite} onEdit={onEdit} />
        {/* FAIR-style estimate: a three-point range for how often and how much, with the reasoning behind it. */}
        <NextSteps
          items={[
            { done: q.frequencyMin != null && q.frequencyMode != null && q.frequencyMax != null, label: "Estimate how often it happens (minimum, likely, maximum)" },
            q.method === "manual"
              ? { done: q.magnitudeMin != null && q.magnitudeMode != null && q.magnitudeMax != null, label: "Estimate the cost of one event (minimum, likely, maximum)" }
              : { done: q.outageHoursMin != null && q.outageHoursMode != null && q.outageHoursMax != null && q.bia?.financialLossPerHour != null, label: "Give the outage hours and the BIA's loss per hour" },
            { done: !!q.rationale?.trim(), label: "Record why these figures", hint: "The sources and assumptions, so the estimate can be challenged and repeated." },
          ]}
          action={canWrite ? <button type="button" style={secondaryBtn} onClick={onEdit}>Fill these in</button> : undefined}
        />
        <DetailSection title="Risk">
          <DetailFields fields={[
            { label: "Risk", value: <PortalLink to="/risk-register" style={{ color: theme.color.accentText, textDecoration: "none" }}>{riskRef(q.risk)} {q.risk.title}</PortalLink>, wide: true },
            { label: "Category", value: q.risk.category },
            { label: "Status", value: q.risk.status.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase()) },
            { label: "Residual score", value: q.risk.residualScore != null ? String(q.risk.residualScore) : null },
          ]} />
        </DetailSection>
        <DetailSection title="Estimate">
          <DetailFields fields={[
            { label: "Events a year (min / likely / max)", value: range(q.frequencyMin, q.frequencyMode, q.frequencyMax, String) },
            { label: "Basis", value: q.method === "bia" ? (q.bia ? `${biaRef(q.bia)} ${q.bia.name}` : "Business impact analysis (removed)") : "Estimate" },
            ...(q.method === "manual" ? [
              { label: "Cost of one event (min / likely / max)", value: range(q.magnitudeMin, q.magnitudeMode, q.magnitudeMax, m) },
            ] : [
              { label: "Loss per hour", value: q.bia?.financialLossPerHour != null ? money(q.bia.financialLossPerHour, q.bia.currency) : null },
              { label: "Outage hours (min / likely / max)", value: range(q.outageHoursMin, q.outageHoursMode, q.outageHoursMax, String) },
              { label: "Fixed costs per event", value: q.fixedCost != null ? m(q.fixedCost) : null },
              { label: "Cost of one event (min / likely / max)", value: q.magnitude ? range(q.magnitude.min, q.magnitude.mode, q.magnitude.max, m) : "Not priced" },
            ]),
            { label: "Currency", value: q.currency },
            { label: "Why these figures", value: q.rationale, wide: true, multiline: true },
          ]} />
        </DetailSection>
        <DetailSection title="Modelled loss">
          {q.summary ? (
            <DetailFields fields={[
              { label: "Average year (ALE)", value: m(q.summary.mean) },
              { label: "Median year (P50)", value: m(q.summary.p50) },
              { label: "One year in ten (P90)", value: m(q.summary.p90) },
              { label: "One year in twenty (P95)", value: m(q.summary.p95) },
              { label: "One year in a hundred (P99)", value: m(q.summary.p99) },
              { label: "Years with a loss", value: pct(q.summary.probabilityOfLoss) },
            ]} />
          ) : (
            <div style={{ ...text.body, color: theme.color.warningText }}>Not priced yet: the cost of one event is missing.</div>
          )}
        </DetailSection>
      </div>
    </ModalOverlay>
  );
}

function ScenarioView({ s, kinds, currency, canWrite, onEdit, onClose }: {
  s: Scenario; kinds: ScenarioKind[]; currency: string; canWrite: boolean; onEdit: () => void; onClose: () => void;
}) {
  const kind = kinds.find((k) => k.id === s.kind)?.label ?? s.kind;
  return (
    <ModalOverlay onClose={onClose}>
      <ModalHeader title={s.name} onClose={onClose} />
      <div style={{ padding: "8px 20px 20px" }}>
        <ViewHeaderActions canWrite={canWrite} onEdit={onEdit} />
        <NextSteps
          items={[
            { done: !!(s.assetId || s.supplierId), label: "Say which asset or supplier fails" },
            ...(s.kind === "data_breach" ? [{ done: s.recordsAffected != null && s.costPerRecord != null, label: "Estimate the records affected and the cost per record" }] : []),
            { done: s.annualProbability != null, label: "Estimate how likely it is in a year" },
            { done: (s.riskIds ?? []).length > 0, label: "Link the risks it plays out" },
            { done: !!s.notes?.trim(), label: "Record the assumptions behind it" },
          ]}
          action={canWrite ? <button type="button" style={secondaryBtn} onClick={onEdit}>Fill these in</button> : undefined}
        />
        <DetailSection title="What happens">
          <DetailFields fields={[
            { label: "Kind", value: kind },
            { label: "Asset", value: s.asset?.name ?? null },
            { label: "Supplier", value: s.supplier?.name ?? null },
            { label: "Outage", value: hoursLabel(s.outageHours) },
            ...(s.kind === "data_breach" ? [
              { label: "Records affected", value: s.recordsAffected != null ? s.recordsAffected.toLocaleString("en-GB") : null },
              { label: "Cost per record", value: s.costPerRecord != null ? money(s.costPerRecord, currency) : null },
            ] : []),
            { label: "Other costs", value: s.extraCosts != null ? money(s.extraCosts, currency) : null },
            { label: "Chance in a year", value: s.annualProbability != null ? pct(s.annualProbability) : null },
            { label: "Notes", value: s.notes, wide: true, multiline: true },
          ]} />
        </DetailSection>
        <DetailSection title="Cost">
          <DetailFields fields={[
            { label: "Total cost", value: money(s.result.total, currency) },
            { label: "Expected a year", value: s.result.expectedAnnual != null ? money(s.result.expectedAnnual, currency) : null },
            { label: "Outage", value: money(s.result.directCost, currency) },
            { label: "Records", value: s.result.recordsCost > 0 ? money(s.result.recordsCost, currency) : null },
            { label: "Other costs", value: s.result.extraCosts > 0 ? money(s.result.extraCosts, currency) : null },
          ]} />
        </DetailSection>
        <DetailSection title="Services hit">
          {s.result.services.length > 0 ? (
            <div style={{ display: "grid", gap: 8 }}>
              {s.result.services.map((svc) => (
                <div key={svc.id} style={{ fontSize: text.body.fontSize }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                    <span style={{ color: theme.color.text }}>{svc.name}</span>
                    <span style={{ color: theme.color.muted, whiteSpace: "nowrap" }}>{svc.lossPerHour != null ? money(svc.cost, svc.currency) : "not priced"}</span>
                  </div>
                  {svc.beyondTolerance && <div style={{ ...text.hint, color: theme.color.dangerText, fontWeight: 600 }}>Beyond tolerance{svc.mtpdHours != null ? `: can be down ${hoursLabel(svc.mtpdHours)} at most` : ""}</div>}
                  {svc.via && <div style={text.meta}>{svc.via}</div>}
                </div>
              ))}
              {s.result.unpricedServices > 0 && <div style={{ fontSize: text.meta.fontSize, color: theme.color.warningText }}>{s.result.unpricedServices} service{s.result.unpricedServices === 1 ? " has" : "s have"} no loss per hour in its business impact analysis, so the outage cost is understated.</div>}
            </div>
          ) : (s.kind === "asset_outage" || s.kind === "supplier_failure" || s.kind === "ransomware") ? (
            <div style={text.hint}>No business process with an impact analysis rests on this. <PortalLink to="/resilience/bia" style={{ color: "var(--c-accentText)" }}>Business impact</PortalLink></div>
          ) : (
            <div style={text.hint}>This kind of scenario is not priced through business processes.</div>
          )}
        </DetailSection>
      </div>
    </ModalOverlay>
  );
}

// ── Quantify modal ────────────────────────────────────────────────────────────

interface QForm {
  riskId: string; fMin: string; fMode: string; fMax: string; method: "manual" | "bia";
  mMin: string; mMode: string; mMax: string; biaId: string; oMin: string; oMode: string; oMax: string; fixedCost: string; currency: string; rationale: string;
}

function toQForm(riskId: string, q: QuantifiedRisk | undefined, currency: string): QForm {
  return {
    riskId, fMin: str(q?.frequencyMin), fMode: str(q?.frequencyMode), fMax: str(q?.frequencyMax), method: q?.method ?? "manual",
    mMin: str(q?.magnitudeMin), mMode: str(q?.magnitudeMode), mMax: str(q?.magnitudeMax), biaId: q?.biaId ?? "",
    oMin: str(q?.outageHoursMin), oMode: str(q?.outageHoursMode), oMax: str(q?.outageHoursMax), fixedCost: str(q?.fixedCost),
    currency: q?.currency ?? currency, rationale: q?.rationale ?? "",
  };
}

function QuantifyModal({ data, quantified, initialRiskId, defaultCurrency, onClose, onSaved }: {
  data: ExposureResponse; quantified: QuantifiedRisk[]; initialRiskId: string | null; defaultCurrency: string; onClose: () => void;
  /** The risk whose quantification was saved, or nothing when it was removed. */
  onSaved: (savedRiskId?: string) => Promise<void>;
}) {
  const byRisk = useMemo(() => new Map(quantified.map((q) => [q.riskId, q])), [quantified]);
  const [f, setF] = useState<QForm>(() => toQForm(initialRiskId ?? "", initialRiskId ? byRisk.get(initialRiskId) : undefined, defaultCurrency));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const set = <K extends keyof QForm>(k: K, v: QForm[K]) => setF((p) => ({ ...p, [k]: v }));
  const existing = f.riskId ? byRisk.get(f.riskId) : undefined;
  const isQuantified = (r: OpenRisk) => !!r.quantification || byRisk.has(r.id);
  const bia = data.bias.find((b) => b.id === f.biaId);

  function pickRisk(id: string) {
    setF(toQForm(id, byRisk.get(id), f.currency || defaultCurrency));
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!f.riskId) { setError("Choose a risk"); return; }
    setSaving(true); setError(null);
    const body = {
      frequencyMin: num(f.fMin) ?? 0, frequencyMode: num(f.fMode) ?? 0, frequencyMax: num(f.fMax) ?? 0,
      method: f.method,
      magnitudeMin: f.method === "manual" ? num(f.mMin) : null, magnitudeMode: f.method === "manual" ? num(f.mMode) : null, magnitudeMax: f.method === "manual" ? num(f.mMax) : null,
      biaId: f.method === "bia" ? f.biaId || null : null,
      outageHoursMin: f.method === "bia" ? num(f.oMin) : null, outageHoursMode: f.method === "bia" ? num(f.oMode) : null, outageHoursMax: f.method === "bia" ? num(f.oMax) : null,
      fixedCost: f.method === "bia" ? num(f.fixedCost) : null,
      currency: (f.currency.trim() || "GBP").toUpperCase(),
      rationale: f.rationale.trim() || null,
    };
    try { await api.put(`/exposure/risks/${f.riskId}/quantification`, body); await onSaved(f.riskId); }
    catch (err) { setError(apiErrorMessage(err, "Could not save the quantification")); setSaving(false); }
  }

  async function remove() {
    if (!f.riskId || !window.confirm("Remove the quantification from this risk? The risk itself stays in the register.")) return;
    setSaving(true); setError(null);
    try { await api.del(`/exposure/risks/${f.riskId}/quantification`); await onSaved(); }
    catch (err) { setError(apiErrorMessage(err, "Could not remove the quantification")); setSaving(false); }
  }

  const toggle = (m: "manual" | "bia", label: string) => (
    <button type="button" onClick={() => set("method", m)} style={{
      ...secondaryBtn, background: f.method === m ? "var(--c-accentBg)" : "transparent", color: f.method === m ? "var(--c-accentText)" : theme.color.muted,
      borderColor: f.method === m ? theme.color.accent : theme.color.borderStrong, fontWeight: f.method === m ? 600 : 400,
    }}>{label}</button>
  );

  return (
    <ModalOverlay onClose={onClose}>
      <ModalHeader title={existing ? "Update a quantification" : "Quantify a risk"} onClose={onClose} />
      <form onSubmit={submit} style={{ padding: 20, display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 14 }}>
        <Field label="Risk *" span2>
          <select required value={f.riskId} onChange={(e) => pickRisk(e.target.value)} style={input}>
            <option value="">— choose an open risk —</option>
            {data.risks.map((r) => <option key={r.id} value={r.id}>{riskRef(r)} {r.title}{isQuantified(r) ? " (quantified)" : ""}</option>)}
          </select>
        </Field>
        {data.risks.length === 0 && <div style={{ gridColumn: "1 / -1", ...text.hint }}>No open risks. <PortalLink to="/risk-register" style={{ color: "var(--c-accentText)" }}>Add one in the risk register</PortalLink> first.</div>}

        <div style={{ gridColumn: "1 / -1", ...text.body, fontWeight: 600 }}>
          Events a year
          <div style={{ ...text.meta, fontWeight: 400 }}>How many times a year could this happen? 0.1 = once in ten years.</div>
        </div>
        <Field label="Minimum"><input type="number" min={0} step="any" required value={f.fMin} onChange={(e) => set("fMin", e.target.value)} style={input} /></Field>
        <Field label="Most likely"><input type="number" min={0} step="any" required value={f.fMode} onChange={(e) => set("fMode", e.target.value)} style={input} /></Field>
        <Field label="Maximum"><input type="number" min={0} step="any" required value={f.fMax} onChange={(e) => set("fMax", e.target.value)} style={input} /></Field>

        <div style={{ gridColumn: "1 / -1", display: "flex", gap: 6, flexWrap: "wrap" }}>
          {toggle("manual", "I will estimate the cost")}
          {toggle("bia", "From a business impact analysis")}
        </div>

        {f.method === "manual" ? (
          <>
            <div style={{ gridColumn: "1 / -1", ...text.meta, marginTop: -6 }}>What one occurrence would cost: response, recovery, lost business, fines, compensation.</div>
            <Field label="Cost of one event, minimum"><input type="number" min={0} step="any" value={f.mMin} onChange={(e) => set("mMin", e.target.value)} style={input} /></Field>
            <Field label="Most likely"><input type="number" min={0} step="any" value={f.mMode} onChange={(e) => set("mMode", e.target.value)} style={input} /></Field>
            <Field label="Maximum"><input type="number" min={0} step="any" value={f.mMax} onChange={(e) => set("mMax", e.target.value)} style={input} /></Field>
          </>
        ) : (
          <>
            <Field label="Business impact analysis" span2 help={bia ? (bia.financialLossPerHour != null ? `Loss per hour: ${money(bia.financialLossPerHour, bia.currency)}` : "This analysis has no loss per hour yet, so the risk cannot be priced from it.") : undefined}>
              <select value={f.biaId} onChange={(e) => set("biaId", e.target.value)} style={input}>
                <option value="">— choose —</option>
                {data.bias.map((b) => <option key={b.id} value={b.id}>{biaRef(b)} {b.name}{b.financialLossPerHour != null ? ` (${money(b.financialLossPerHour, b.currency)} an hour)` : " (no loss per hour)"}</option>)}
              </select>
            </Field>
            <Field label="Fixed costs" help="Per event, on top of the outage."><input type="number" min={0} step="any" value={f.fixedCost} onChange={(e) => set("fixedCost", e.target.value)} style={input} /></Field>
            {data.bias.length === 0 && <div style={{ gridColumn: "1 / -1", ...text.hint }}>No business impact analyses yet. <PortalLink to="/resilience/bia" style={{ color: "var(--c-accentText)" }}>Create one</PortalLink> to price outages from it.</div>}
            <Field label="Outage hours, minimum"><input type="number" min={0} step="any" value={f.oMin} onChange={(e) => set("oMin", e.target.value)} style={input} /></Field>
            <Field label="Most likely"><input type="number" min={0} step="any" value={f.oMode} onChange={(e) => set("oMode", e.target.value)} style={input} /></Field>
            <Field label="Maximum"><input type="number" min={0} step="any" value={f.oMax} onChange={(e) => set("oMax", e.target.value)} style={input} /></Field>
          </>
        )}

        <Field label="Currency"><input maxLength={3} value={f.currency} onChange={(e) => set("currency", e.target.value.toUpperCase())} style={input} /></Field>
        <Field label="Why these figures" span2 help="Sources, incidents, industry data — so the next reviewer can challenge them."><textarea rows={2} value={f.rationale} onChange={(e) => set("rationale", e.target.value)} style={textarea} /></Field>

        {existing?.summary && (
          <div style={{ gridColumn: "1 / -1", ...text.hint }}>
            Currently: {money(existing.summary.mean, existing.currency)} a year on average, {money(existing.summary.p90, existing.currency)} one year in ten, a loss in {pct(existing.summary.probabilityOfLoss)} of years.
          </div>
        )}
        {error && <div style={{ gridColumn: "1 / -1", ...text.body, color: theme.color.dangerText }}>{error}</div>}
        <div style={{ gridColumn: "1 / -1", display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button type="submit" disabled={saving} style={{ ...btnPrimary, opacity: saving ? 0.6 : 1 }}>{saving ? "Saving…" : "Save"}</button>
          <button type="button" style={secondaryBtn} onClick={onClose}>Cancel</button>
          {existing && <button type="button" style={{ ...dangerBtn, marginLeft: "auto" }} disabled={saving} onClick={remove}>Remove quantification</button>}
        </div>
      </form>
    </ModalOverlay>
  );
}

// ── Scenario modal ────────────────────────────────────────────────────────────

function ScenarioModal({ data, existing, onClose, onSaved }: {
  data: ExposureResponse; existing: Scenario | null; onClose: () => void;
  /** true when the scenario still exists (saved), false when it was deleted. */
  onSaved: (kept: boolean) => Promise<void>;
}) {
  const [name, setName] = useState(existing?.name ?? "");
  const [kind, setKind] = useState(existing?.kind ?? data.scenarioKinds[0]?.id ?? "asset_outage");
  const [assetId, setAssetId] = useState(existing?.assetId ?? "");
  const [supplierId, setSupplierId] = useState(existing?.supplierId ?? "");
  const [outageHours, setOutageHours] = useState(str(existing?.outageHours ?? 24));
  const [records, setRecords] = useState(str(existing?.recordsAffected));
  const [costPerRecord, setCostPerRecord] = useState(str(existing?.costPerRecord));
  const [extraCosts, setExtraCosts] = useState(str(existing?.extraCosts));
  const [probability, setProbability] = useState(str(existing?.annualProbability));
  const [notes, setNotes] = useState(existing?.notes ?? "");
  const [riskIds, setRiskIds] = useState<string[]>(existing?.riskIds ?? []);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const help = data.scenarioKinds.find((k) => k.id === kind)?.help;

  async function submit(e: React.FormEvent) {
    e.preventDefault(); setSaving(true); setError(null);
    const body = {
      name: name.trim(), kind,
      assetId: kind === "asset_outage" ? assetId || null : null,
      supplierId: kind === "supplier_failure" ? supplierId || null : null,
      outageHours: num(outageHours) ?? 0,
      recordsAffected: kind === "data_breach" ? (num(records) != null ? Math.round(num(records)!) : null) : null,
      costPerRecord: kind === "data_breach" ? num(costPerRecord) : null,
      extraCosts: num(extraCosts),
      annualProbability: num(probability),
      notes: notes.trim() || null,
      riskIds,
    };
    try {
      if (existing) await api.put(`/exposure/scenarios/${existing.id}`, body); else await api.post("/exposure/scenarios", body);
      await onSaved(true);
    } catch (err) { setError(apiErrorMessage(err, "Could not save the scenario")); setSaving(false); }
  }

  async function remove() {
    if (!existing || !window.confirm(`Delete the scenario "${existing.name}"?`)) return;
    setSaving(true); setError(null);
    try { await api.del(`/exposure/scenarios/${existing.id}`); await onSaved(false); }
    catch (err) { setError(apiErrorMessage(err, "Could not delete the scenario")); setSaving(false); }
  }

  return (
    <ModalOverlay onClose={onClose}>
      <ModalHeader title={existing ? "Edit scenario" : "Add a scenario"} onClose={onClose} />
      <form onSubmit={submit} style={{ padding: 20, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 14 }}>
        <Field label="Name *" span2><input required maxLength={200} value={name} onChange={(e) => setName(e.target.value)} style={input} placeholder="e.g. Data centre loses power for a day" /></Field>
        <Field label="What happens *" span2 help={help}>
          <select value={kind} onChange={(e) => setKind(e.target.value)} style={input}>
            {data.scenarioKinds.map((k) => <option key={k.id} value={k.id}>{k.label}</option>)}
          </select>
        </Field>
        {kind === "asset_outage" && (
          <Field label="Asset that is unavailable *" span2>
            <select required value={assetId} onChange={(e) => setAssetId(e.target.value)} style={input}>
              <option value="">— choose —</option>
              {data.assets.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </Field>
        )}
        {kind === "supplier_failure" && (
          <Field label="Supplier that fails *" span2>
            <select required value={supplierId} onChange={(e) => setSupplierId(e.target.value)} style={input}>
              <option value="">— choose —</option>
              {data.suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </Field>
        )}
        <Field label="Outage (hours) *" help="How long the services it hits stay down."><input type="number" min={0} step="any" required value={outageHours} onChange={(e) => setOutageHours(e.target.value)} style={input} /></Field>
        {kind === "data_breach" && (
          <>
            <Field label="Records affected"><input type="number" min={0} step={1} value={records} onChange={(e) => setRecords(e.target.value)} style={input} /></Field>
            <Field label="Cost per record" help="Notification, support, claims, per person."><input type="number" min={0} step="any" value={costPerRecord} onChange={(e) => setCostPerRecord(e.target.value)} style={input} /></Field>
          </>
        )}
        <Field label="Other costs" help="Response, legal, fines, anything not counted above."><input type="number" min={0} step="any" value={extraCosts} onChange={(e) => setExtraCosts(e.target.value)} style={input} /></Field>
        <Field label="Chance in a year" help="0.05 = once in twenty years. Leave blank if you do not want an annual figure."><input type="number" min={0} max={1} step="any" value={probability} onChange={(e) => setProbability(e.target.value)} style={input} /></Field>
        <Field label="Notes" span2><textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} style={textarea} /></Field>
        {data.risks.length > 0 && (
          <Field label="Risks this scenario prices" span2 help="Optional. Hold Ctrl (Cmd on a Mac) to choose more than one; the risks show the scenario among their links.">
            <select multiple size={4} value={riskIds} onChange={(e) => setRiskIds(Array.from(e.target.selectedOptions).map((o) => o.value))} style={{ ...input, height: "auto" }}>
              {data.risks.map((r) => <option key={r.id} value={r.id}>{r.displayId != null ? `RISK-${String(r.displayId).padStart(3, "0")} ` : ""}{r.title}</option>)}
            </select>
          </Field>
        )}
        {error && <div style={{ gridColumn: "1 / -1", ...text.body, color: theme.color.dangerText }}>{error}</div>}
        <div style={{ gridColumn: "1 / -1", display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button type="submit" disabled={saving} style={{ ...btnPrimary, opacity: saving ? 0.6 : 1 }}>{saving ? "Saving…" : "Save"}</button>
          <button type="button" style={secondaryBtn} onClick={onClose}>Cancel</button>
          {existing && <button type="button" style={{ ...dangerBtn, marginLeft: "auto" }} disabled={saving} onClick={remove}>Delete</button>}
        </div>
      </form>
    </ModalOverlay>
  );
}
